import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCameraRetro } from "@fortawesome/free-solid-svg-icons";
import { Alert, Snackbar } from "@mui/material";
import moment from "moment/moment";
import { useTranslation } from "react-i18next";
import { updateclub, allclublist, getClubLicenseUsage } from "../../components/funtionsBD/Mainservice";
import { AddHeader } from "../../components/AddHeader";
import { useLocation, useNavigate } from "react-router-dom";
import AdminLayout from "../../components/AdminLayout";
import ClubLogoMark from "../../components/ClubLogoMark";
import ClubLicenseSettingsCard from "../../components/superadmin/ClubLicenseSettingsCard";
import { CLUB_COUNTRIES, normalizeClubCountry } from "../../constants/countries";
import { useBrand } from "../../context/BrandContext";
import { validateClubLogoFile } from "../../utils/clubLogoFile";

// Helper function to convert ISO date (YYYY-MM-DD) to DD/MM/YYYY
const formatDateToDDMMYYYY = (isoDate) => {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Helper function to convert DD/MM/YYYY to ISO date (YYYY-MM-DD)
const parseDateFromDDMMYYYY = (ddmmyyyy) => {
  if (!ddmmyyyy || !ddmmyyyy.trim()) return null;
  const parts = ddmmyyyy.trim().split('/');
  if (parts.length !== 3) return null;
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
  const year = parseInt(parts[2], 10);
  
  if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
  if (day < 1 || day > 31 || month < 0 || month > 11 || year < 1900 || year > 2100) return null;
  
  const date = new Date(year, month, day);
  // Verify the date is valid (handles cases like 31/02/2023)
  if (date.getDate() !== day || date.getMonth() !== month || date.getFullYear() !== year) {
    return null;
  }
  
  // Return in YYYY-MM-DD format
  const isoYear = date.getFullYear();
  const isoMonth = String(date.getMonth() + 1).padStart(2, '0');
  const isoDay = String(date.getDate()).padStart(2, '0');
  return `${isoYear}-${isoMonth}-${isoDay}`;
};

export const UpdateClub = () => {
  const [pictures, setPictures] = useState([]);
  const [imgvalue, setimgvalue] = useState();
  const [Type, setType] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [success, setsuccess] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { t } = useTranslation();
  const { refreshBrand } = useBrand();
  const location = useLocation();
  const navigate = useNavigate();
  const info = JSON.parse(localStorage.getItem("user"));
  const isSuperAdmin = Boolean(
    info?.type_user_id === 1 ||
    info?.role_id === "r1" ||
    info?.primaryRole?.role_name === "Super Admin" ||
    info?.role_name === "Super Admin" ||
    info?.isSuperAdmin ||
    info?.permissions?.includes("superadmin")
  );
  
  // Get club data from location state (if navigating from club list) or fetch from API (if admin)
  const [clubData, setClubData] = useState(location.state?.info || null);
  const [monthlyEvalUsed, setMonthlyEvalUsed] = useState(null);
  
  const validate = (values) => {
    const errors = {};

    if (!values.name) {
      errors.name = `${t("require")}`;
    } else if (values.name.length > 100) {
      errors.name = `${t("must")}`;
    }

    if (!values.email) {
      errors.email = `${t("require")}`;
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
      errors.email = t("invalidEmail") || "Invalid email address";
    }

    if (!values.address) {
      errors.address = `${t("require")}`;
    }

    if (!values.country) {
      errors.country = `${t("require")}`;
    }

    // Validate website URL format if provided
    if (values.website && values.website.trim() !== "") {
      const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/i;
      if (!urlPattern.test(values.website)) {
        errors.website = t("invalidWebsite") || "Invalid website URL";
      }
    }

    // Validate founded date format (DD/MM/YYYY)
    if (values.foundedDate && values.foundedDate.trim() !== "") {
      const parsedDate = parseDateFromDDMMYYYY(values.foundedDate);
      if (!parsedDate) {
        errors.foundedDate = t("invalidDate") || "Invalid date format. Use DD/MM/YYYY";
      } else {
        // Check if date is not in the future
        const dateObj = new Date(parsedDate);
        const today = new Date();
        today.setHours(23, 59, 59, 999); // End of today
        if (dateObj > today) {
          errors.foundedDate = t("dateCannotBeFuture") || "Date cannot be in the future";
        }
      }
    }

    return errors;
  };

  const closealert = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  const fetchClubData = async () => {
    try {
      setLoading(true);
      const response = await allclublist();
      if (response.data.status === "success") {
        // Find the club that matches the admin's club_id
        const club = response.data.data.find(c => c.id === info.club_id || c.club_id === info.club_id);
        if (club) {
          setClubData(club);
        } else {
          setError("Club not found");
        }
      }
    } catch (error) {
      console.error("Error fetching club data:", error);
      setError("Failed to load club information");
    } finally {
      setLoading(false);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const check = validateClubLogoFile(file);
      if (!check.ok) {
        setError(t(check.messageKey));
        setOpen(true);
        e.target.value = "";
        return;
      }
    }
    setimgvalue(e.target.value);
    setPictures(file);
    if (file) {
      formik.setFieldValue("img", URL.createObjectURL(file));
    }
  };

  const handleadd = async (values, { resetForm }) => {
    if (!clubData) {
      setError("Club data not loaded");
      return;
    }
    
    try {
      setLoading(true);
      setError("");
      const clubId = clubData.club_id || clubData.id;
      
      // Convert DD/MM/YYYY to ISO format (YYYY-MM-DD) for backend
      const foundedDateISO = values.foundedDate ? parseDateFromDDMMYYYY(values.foundedDate) : null;
      
      // Prepare info object with all fields
      const updateInfo = {
        name: values.name,
        email: values.email,
        address: values.address,
        phone: values.phone || "",
        website: values.website || "",
        foundedDate: foundedDateISO,
        country: normalizeClubCountry(values.country),
        license_tier: values.license_tier || "base",
        addon_hr: values.addon_hr || false,
      };
      
      const response = await updateclub(pictures, updateInfo, clubId);
      setsuccess(response.data.message || t("clubUpdatedSuccessfully"));
      setOpen(true);

      if (typeof refreshBrand === "function") {
        await refreshBrand();
      }
      
      // Refresh club data after update
      if (info?.club_id) {
        await fetchClubData();
      }
      
      // Navigate back to club list after 2 seconds
      setTimeout(() => {
        navigate("/Club");
      }, 2000);
    } catch (error) {
      console.error("Error updating club:", error);
      setError(error.response?.data?.message || error.message || t("failedToUpdateClub"));
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Initialize formik with default values - will be updated when clubData is loaded
  const formik = useFormik({
    initialValues: {
      name: clubData?.club_name || "",
      email: clubData?.club_email || "",
      img: clubData?.club_image || "",
      address: clubData?.club_address || "",
      phone: clubData?.phone_mobile || "",
      website: clubData?.club_website || "",
      foundedDate: clubData?.founded_date ? formatDateToDDMMYYYY(clubData.founded_date) : "",
      country: normalizeClubCountry(clubData?.country) || "LU",
      license_tier: clubData?.license_tier || "base",
      addon_hr: Boolean(clubData?.addon_hr),
    },
    enableReinitialize: true, // Allow formik to reinitialize when clubData changes
    validate,
    onSubmit: (values, { resetForm }) => {
      handleadd(values, { resetForm });
    },
  });

  // Update formik values when clubData is loaded
  useEffect(() => {
    if (clubData) {
      formik.setValues({
        name: clubData.club_name || "",
        email: clubData.club_email || "",
        img: clubData.club_image || "",
        address: clubData.club_address || "",
        phone: clubData.phone_mobile || "",
        website: clubData.club_website || "",
        foundedDate: clubData.founded_date ? formatDateToDDMMYYYY(clubData.founded_date) : "",
        country: normalizeClubCountry(clubData.country) || "LU",
        license_tier: clubData.license_tier || "base",
        addon_hr: Boolean(clubData.addon_hr),
      });
    }
  }, [clubData]);

  useEffect(() => {
    // If no club data in location state and user is club admin, fetch their club
    if (!clubData && info?.club_id) {
      fetchClubData();
    }
  }, []);

  useEffect(() => {
    const clubId = clubData?.club_id;
    if (!clubId || !isSuperAdmin) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const license = await getClubLicenseUsage(clubId);
        if (!cancelled) {
          setMonthlyEvalUsed(
            license?.monthly_eval_used != null
              ? Number(license.monthly_eval_used)
              : null
          );
        }
      } catch (err) {
        console.warn("Failed to load eval usage:", err?.message || err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clubData?.club_id, isSuperAdmin]);

  // If no club data, show loading or error (after all hooks are called)
  if (!clubData) {
    if (loading) {
      return (
        <AdminLayout>
          <div>Loading...</div>
        </AdminLayout>
      );
    }
    if (error) {
      return (
        <AdminLayout>
          <Alert severity="error">{error}</Alert>
        </AdminLayout>
      );
    }
    return null;
  }
  return (
    <AdminLayout>
      <AddHeader title={t("editclub")} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <form className="add-exercise" onSubmit={formik.handleSubmit}>
        <div className="edit-pic">
          {formik.values.img ? (
            <img src={formik.values.img} alt="" />
          ) : (
            <ClubLogoMark
              club={clubData}
              useBrandFallback
              size="lg"
            />
          )}
          <label htmlFor="img" className="edit-img-label">
            <FontAwesomeIcon icon={faCameraRetro} className="edit-camera" />
          </label>
        </div>
        <input
          className="input_addpicsvg"
          type="file"
          name="img"
          value={imgvalue}
          id="img"
          accept="image/*"
          onChange={handleFileInputChange}
        />
        <div className="input-details">
          <div className="input-detail">
            <label htmlFor=""> {t("clubname")}</label>
            <input
              placeholder={t("writename")}
              type="text"
              value={formik.values.name}
              name="name"
              onChange={formik.handleChange}
            />
          </div>
          {formik.errors.name ? (
            <div className="validation_error">{formik.errors.name}</div>
          ) : null}

          <div className="input-detail">
            <label htmlFor=""> {t("mail")}</label>
            <input
              type="email"
              placeholder={t("enteremail")}
              value={formik.values.email}
              onChange={formik.handleChange}
              name="email"
            />
          </div>

          {formik.errors.email ? (
            <div className="validation_error">{formik.errors.email}</div>
          ) : null}

          <div className="input-detail">
            <label htmlFor=""> {t("address")}</label>
            <input
              placeholder={t("address")}
              type="text"
              value={formik.values.address}
              name="address"
              onChange={formik.handleChange}
            />
          </div>
          {formik.errors.address ? (
            <div className="validation_error">{formik.errors.address}</div>
          ) : null}

          <div className="input-detail">
            <label htmlFor="country">{t("country") || "Country"}</label>
            <select
              id="country"
              name="country"
              value={formik.values.country}
              onChange={formik.handleChange}
            >
              <option value="">{t("selectCountry") || "Select Country"}</option>
              {formik.values.country &&
                !CLUB_COUNTRIES.some((c) => c.code === formik.values.country) && (
                  <option value={formik.values.country}>{formik.values.country}</option>
                )}
              {CLUB_COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name} ({c.code})
                </option>
              ))}
            </select>
          </div>
          {formik.errors.country ? (
            <div className="validation_error">{formik.errors.country}</div>
          ) : null}

          <div className="input-detail">
            <label htmlFor=""> {t("phone") || "Phone"}</label>
            <input
              placeholder={t("enterPhone") || "Enter phone number"}
              type="tel"
              value={formik.values.phone}
              name="phone"
              onChange={formik.handleChange}
            />
          </div>

          <div className="input-detail">
            <label htmlFor=""> {t("website") || "Website"}</label>
            <input
              placeholder={t("enterWebsite") || "Enter website URL (e.g., https://example.com)"}
              type="url"
              value={formik.values.website}
              name="website"
              onChange={formik.handleChange}
            />
          </div>
          {formik.errors.website ? (
            <div className="validation_error">{formik.errors.website}</div>
          ) : null}

          <div className="input-detail">
            <label htmlFor=""> {t("foundedDate") || "Founded Date"} (DD/MM/YYYY)</label>
            <input
              type="text"
              placeholder="DD/MM/YYYY"
              value={formik.values.foundedDate}
              name="foundedDate"
              onChange={(e) => {
                // Allow only numbers and slashes
                let value = e.target.value.replace(/[^\d/]/g, '');
                // Auto-format as user types: DD/MM/YYYY
                if (value.length > 2 && !value.includes('/')) {
                  value = value.slice(0, 2) + '/' + value.slice(2);
                }
                if (value.length > 5 && value.split('/').length === 2) {
                  value = value.slice(0, 5) + '/' + value.slice(5);
                }
                // Limit to 10 characters (DD/MM/YYYY)
                if (value.length <= 10) {
                  formik.setFieldValue('foundedDate', value);
                }
              }}
              onBlur={(e) => {
                // Validate and format on blur
                const value = e.target.value.trim();
                if (value && !parseDateFromDDMMYYYY(value)) {
                  // Invalid date - keep as is to show error
                  formik.setFieldTouched('foundedDate', true);
                }
              }}
            />
          </div>
          {formik.errors.foundedDate ? (
            <div className="validation_error">{formik.errors.foundedDate}</div>
          ) : null}
        </div>

        {/* Global Super Admin Pilot License & Feature Set Settings */}
        <ClubLicenseSettingsCard
          tier={formik.values.license_tier}
          addonHr={formik.values.addon_hr}
          readOnly={!isSuperAdmin}
          monthlyEvalUsed={monthlyEvalUsed}
          onChange={({ tier, addonHr }) => {
            formik.setFieldValue("license_tier", tier);
            formik.setFieldValue("addon_hr", addonHr);
          }}
        />

        <div className="input-detail">
          <button className="w-100" type="submit" disabled={loading}>
            {loading ? t("updating") || "Updating..." : t("updateclub")}
          </button>
        </div>
        <Snackbar
          open={open}
          autoHideDuration={3000}
          onClose={closealert}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
        >
          <Alert variant="filled" severity="success">
            {success}
          </Alert>
        </Snackbar>
      </form>
    </AdminLayout>
  );
};
